import type * as React from "react";
import { Clock, MapPin, Users } from "lucide-react";
import { cn } from "../../../lib/cn";
import { CitationHighlighter } from "../CitationHighlighter";
import type { CitationAnchor } from "../types";

export interface CalendarAttendee {
  email: string;
  name?: string;
  rsvp?: "accepted" | "declined" | "tentative" | "needs_action";
}

export interface CalendarEventPayload {
  title: string;
  start: string;
  end: string;
  location?: string;
  description?: string;
  attendees?: CalendarAttendee[];
}

export interface EventCardProps {
  payload: CalendarEventPayload;
  anchor?: CitationAnchor;
}

function formatRange(start: string, end: string): string {
  const s = new Date(start);
  const e = new Date(end);
  const sameDay = s.toDateString() === e.toDateString();
  const fmtDate = s.toLocaleDateString();
  const fmtTime = (d: Date) =>
    d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return sameDay
    ? `${fmtDate} · ${fmtTime(s)} → ${fmtTime(e)}`
    : `${s.toLocaleString()} → ${e.toLocaleString()}`;
}

/**
 * EventCard — calendar event renderer for the Source Drawer.
 *
 * Highlight state (`anchor` present) wraps the whole card in the
 * `CitationHighlighter` chrome — the event itself is the citation.
 */
export const EventCard: React.FC<EventCardProps> = ({ payload, anchor }) => {
  const cited = Boolean(anchor);
  const body = (
    <article className={cn("flex flex-col gap-3 text-sm")}>
      <header>
        <div className="font-display font-semibold text-ink-1 text-lg tracking-tight">
          {payload.title}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-ink-2 text-xs">
          <span className="inline-flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5" aria-hidden />
            <time className="font-mono tabular-nums">
              {formatRange(payload.start, payload.end)}
            </time>
          </span>
          {payload.location ? (
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" aria-hidden />
              {payload.location}
            </span>
          ) : null}
        </div>
      </header>

      {payload.description ? (
        <p className="whitespace-pre-wrap text-ink-2 text-sm">
          {payload.description}
        </p>
      ) : null}

      {payload.attendees?.length ? (
        <section>
          <div className="mb-1 flex items-center gap-1.5 font-medium text-[10px] text-ink-3 uppercase tracking-wide">
            <Users className="h-3 w-3" aria-hidden />
            Partecipanti ({payload.attendees.length})
          </div>
          <ul className="flex flex-wrap gap-1.5">
            {payload.attendees.map((a) => (
              <li
                key={a.email}
                className={cn(
                  "rounded-full border border-hairline bg-surface-1/70 px-2 py-0.5",
                  "text-[11px] text-ink-2",
                  a.rsvp === "accepted" && "border-ando-orange/40 text-ando-orange",
                )}
                title={a.rsvp}
              >
                {a.name ?? a.email}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </article>
  );

  return <CitationHighlighter active={cited}>{body}</CitationHighlighter>;
};
EventCard.displayName = "EventCard";
